import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Layout from "../../common/components/Layout";
import { fetchEmployees } from "../../store/slices/employeeSlice";
import { getProfile } from "../../store/slices/Auth/authSlice";

// 1. Import your count async thunk action creators
import { fetchEmployeeCount } from "../../store/slices/Dashboard/employeeCountSlice";
import { fetchLeaveHierarchyCount } from "../../store/slices/Dashboard/pendingLeaveSlice";

const StatCard = ({ title, count, link, icon, color }) => (
  <div className="col-md-4">
    <Link to={link} className="text-decoration-none">
      <div
        className="p-4 h-100 transition-up"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(12px)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
        }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "45px",
              height: "45px",
              backgroundColor: `${color}15`,
              color: color,
            }}
          >
            <i className={`bi ${icon} fs-5`}></i>
          </div>
          <i className="bi bi-arrow-up-right text-muted small"></i>
        </div>
        <h6
          className="text-muted fw-bold small text-uppercase mb-1"
          style={{ fontSize: "0.65rem", letterSpacing: "1px" }}
        >
          {title}
        </h6>
        <h2 className="fw-bold m-0 text-dark" style={{ letterSpacing: "-1px" }}>
          {count}
        </h2>
      </div>
    </Link>
  </div>
);

const Dashboard = () => {
  const dispatch = useDispatch();

  const { user, accessToken, companyId, branchId } = useSelector(
    (state) => state.auth,
  );

  // 2. Map selectors out of your custom state nodes
  const { count: totalEmployeeCount, status: countStatus } = useSelector(
    (state) => state.employeeCount,
  );

  // const { count: pendingLeaveCount, status: leaveCountStatus } = useSelector(
  //   (state) => state.leaveHierarchyCount,
  // );

  useEffect(() => {
    if (accessToken && !user) {
      dispatch(getProfile());
    }
  }, [dispatch, accessToken, user]);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchEmployees({ companyId }));
      dispatch(fetchEmployeeCount({ companyId, branchId }));
      dispatch(fetchLeaveHierarchyCount({ companyId, branchId, status: "pending" }));
    }
  }, [dispatch, companyId, branchId]);

  return (
    <Layout>
      <style>{`
        .transition-up:hover { 
          transform: translateY(-5px); 
          box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
          background-color: rgba(255, 255, 255, 0.9) !important;
        }
      `}</style>

      <div className="container-fluid py-2">
        {/* Header */}
        <div className="mb-4 pt-2">
          <h1
            className="fw-bold text-dark mb-1"
            style={{ letterSpacing: "-1.5px" }}
          >
            Overview
          </h1>
          <p className="text-muted small">
            Workforce activity for{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Top Metrics */}
        <div className="row g-4 mb-4">
          <StatCard
            title="Total Employees"
            count={countStatus === "loading" ? "..." : totalEmployeeCount}
            link="/employee"
            icon="bi-people"
            color="#6366f1"
          />
          <StatCard
            title="Pending Leaves"
            // count={leaveCountStatus === "loading" ? "..." : pendingLeaveCount}
            count="N/A"
            // link="/leave-management/approval"
            icon="bi-calendar-event"
            color="#f59e0b"
          />
          <StatCard
            title="Recent Hires"
            count="0"
            link="/employee"
            icon="bi-person-plus"
            color="#10b981"
          />
        </div>

        <div className="row g-4">
          {/* Announcement Section */}
          <div className="col-lg-8">
            <div
              className="p-4 h-100"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold m-0">Announcements</h5>
                <button className="btn btn-link btn-sm text-decoration-none text-primary fw-bold p-0">
                  View All
                </button>
              </div>
              <div className="text-center py-5">
                <div className="mb-3">
                  <i
                    className="bi bi-megaphone text-muted opacity-25"
                    style={{ fontSize: "3.5rem" }}
                  ></i>
                </div>
                <p className="text-muted fw-medium">
                  No active announcements at the moment.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats Column */}
          <div className="col-lg-4">
            <div className="d-flex flex-column gap-4 h-100">
              {/* Dark Card */}
              <div
                className="p-4 flex-fill d-flex flex-column justify-content-center"
                style={{
                  backgroundColor: "#0f172a",
                  borderRadius: "16px",
                  color: "#fff",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                }}
              >
                <h6
                  className="small text-uppercase opacity-50 mb-3 fw-bold"
                  style={{ letterSpacing: "1px" }}
                >
                  Upcoming Holidays
                </h6>
                <div className="d-flex align-items-center gap-3">
                  <div className="display-5 fw-bold text-primary">0</div>
                  <div className="small opacity-75 fw-medium">
                    No public holidays
                    <br />
                    this week
                  </div>
                </div>
              </div>

              {/* Light Glass Card */}
              <div
                className="p-4 flex-fill d-flex flex-column justify-content-center"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "16px",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <h6
                  className="small text-uppercase text-muted mb-3 fw-bold"
                  style={{ letterSpacing: "1px" }}
                >
                  Birthdays
                </h6>
                <div className="d-flex align-items-center gap-3">
                  <div className="display-5 fw-bold text-dark">0</div>
                  <div className="small text-muted fw-medium">
                    No celebrants
                    <br />
                    today
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;